import { useCallback, useEffect, useState } from 'react'
import captchaService, { CaptchaConfig } from '../services/captchaService'
import { isCaptchaRequiredError } from '../utils/captchaErrors'

/**
 * Loads /auth/captcha-config and tracks hCaptcha token for login/register forms.
 * Widget is shown when captcha is enabled, or after the API returns requires_captcha.
 * Client-side "complete captcha" only applies once the widget has actually rendered.
 */
export function useAuthCaptcha() {
  const [config, setConfig] = useState<CaptchaConfig | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [forced, setForced] = useState(false)
  const [siteKeyOverride, setSiteKeyOverride] = useState<string | null>(null)
  const [widgetReady, setWidgetReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    void captchaService.refreshConfig().then((cfg) => {
      if (!cancelled) setConfig(cfg)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const siteKey = siteKeyOverride || config?.site_key || ''
  const showWidget = Boolean(siteKey && ((config?.enabled && config.site_key) || forced))

  const onTokenChange = useCallback((next: string | null) => {
    setToken(next)
  }, [])

  const onReadyChange = useCallback((ready: boolean) => {
    setWidgetReady(ready)
  }, [])

  const missingTokenMessage = (): string | null => {
    // Only block when the checkbox is actually on screen and unfinished.
    if (showWidget && widgetReady && !token) {
      return 'Please complete the CAPTCHA verification.'
    }
    return null
  }

  const applyCaptchaRequired = (siteKeyFromError?: string | null) => {
    setForced(true)
    setWidgetReady(false)
    if (siteKeyFromError) {
      setSiteKeyOverride(siteKeyFromError)
    }
  }

  const handleAuthError = (err: unknown): boolean => {
    if (!isCaptchaRequiredError(err)) return false
    applyCaptchaRequired(
      typeof (err as { captchaSiteKey?: string }).captchaSiteKey === 'string'
        ? (err as { captchaSiteKey: string }).captchaSiteKey
        : null,
    )
    return true
  }

  const resetToken = async () => {
    setToken(null)
    try {
      await captchaService.resetCaptcha()
    } catch {
      // ignore
    }
  }

  return {
    showWidget,
    siteKey,
    captchaToken: token,
    widgetReady,
    onTokenChange,
    onReadyChange,
    missingTokenMessage,
    handleAuthError,
    applyCaptchaRequired,
    resetToken,
  }
}
